import React, { useState } from 'react';
import SectionHeader from './SectionHeader';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import Typewriter from './Typewriter';

const About = () => {
  const [iframeLoaded, setIframeLoaded] = useState(false);

  return (
    <section id="about" className="py-24 bg-obsidian relative overflow-hidden">
      <div className="absolute top-[65%] md:top-[60%] left-[50%] md:left-[40%] -translate-x-1/2 -translate-y-1/2 w-[300px] h-[400px] md:w-[650px] md:h-[750px] z-0 opacity-30 md:opacity-50 pointer-events-none transition-all duration-700 overflow-hidden">
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: iframeLoaded ? 1 : 0.2 }}
          className="absolute w-[calc(100%+80px)] h-[calc(100%+80px)] -bottom-10 -right-10"
        >
          <iframe 
            src='https://my.spline.design/sidebot-EnhTIcejq05BEbAaqoBA4woW/' 
            frameBorder='0' 
            width='100%' 
            height='100%' 
            title="Sidebot AI Companion"
            onLoad={() => setIframeLoaded(true)}
            loading="lazy"
          ></iframe>
        </motion.div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-4xl mx-auto">
          <SectionHeader 
            title="About MidnightNeuron" 
            subtitle="Bridging the gap between theoretical AI and production software." 
            align="center" 
          />
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8 md:mt-12 items-start">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="md:col-span-2 text-gray-300 text-sm md:text-base leading-relaxed space-y-6 bg-obsidian/60 backdrop-blur-md p-6 md:p-8 rounded-lg border border-white/5 order-2 md:order-1"
            >
              <Typewriter as="p" text="MidnightNeuron is a multidisciplinary engineering studio obsessed with the intersection of Artificial Intelligence and User Experience." speed={20} className="block" />
              <Typewriter as="p" text="Our background isn't just in training models; it's in building the robust systems that house them. We treat code like a craft and AI like a raw material that needs to be refined into useful tools." speed={20} delay={1000} className="block" />
              <Typewriter as="p" text="Currently exploring agentic workflows and multi-modal interactions. Our goal is to build software that doesn't just process data, but understands intent." speed={20} delay={2500} className="block" />
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-carbon/90 border border-white/10 p-6 relative backdrop-blur-md self-start order-1 md:order-2"
            >
              <div className="absolute top-0 left-0 w-full h-1 bg-neon-cyan"></div>
              <h3 className="font-orbitron font-bold text-white mb-6 flex items-center gap-2">
                <SafeIcon icon={FiIcons.FiCpu} />
                <Typewriter text="SPECIFICATIONS" delay={500} />
              </h3>
              <ul className="space-y-4 font-mono text-xs md:text-sm">
                <li className="flex justify-between border-b border-white/5 pb-2">
                  <span className="text-gray-500">CLASS</span>
                  <span className="text-neon-cyan">AI Engineering</span>
                </li>
                <li className="flex justify-between border-b border-white/5 pb-2">
                  <span className="text-gray-500">BASE</span>
                  <span className="text-white">Global / Digital</span>
                </li>
                <li className="flex justify-between border-b border-white/5 pb-2">
                  <span className="text-gray-500">CAPACITY</span>
                  <span className="text-white">End-to-End</span>
                </li>
                <li className="pt-2">
                  <span className="text-gray-500 block mb-2">CORE STACK</span>
                  <div className="flex flex-wrap gap-2 text-[10px] text-white">
                    <span className="bg-white/10 px-1 border border-white/5">Python</span>
                    <span className="bg-white/10 px-1 border border-white/5">TypeScript</span>
                    <span className="bg-white/10 px-1 border border-white/5">PyTorch</span>
                    <span className="bg-white/10 px-1 border border-white/5">React</span>
                    <span className="bg-white/10 px-1 border border-white/5">AWS</span>
                  </div>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;